import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { Add, Close, Refresh } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { BackdropForDialog, BrandColorPicker, FileDropzone } from 'components';
import { useBuilding, useUpdateBuildingMutation, useDeleteBuildingLogoMutation } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices';
import { useAppSelector } from 'store/store.hook';

type Image = {
  url?: string;
  file?: File | null;
};

const FILE_SIZE = 10000000; // 10 MB
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'];

interface BuildingCustomizationFormProps {
  buildingId: number;
  next?: () => void;
}

export const BuildingCustomizationForm = ({ buildingId, next = () => {} }: BuildingCustomizationFormProps) => {
  const theme = useTheme();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [mutate, { isLoading, isSuccess, isError }] = useUpdateBuildingMutation();
  const { data: building, isLoading: loading_form } = useBuilding({ buildingId });
  const [deleteLogo] = useDeleteBuildingLogoMutation();

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues: {
      logo: building.logoUrl ? ({ url: building.logoUrl } as Image) : { url: '' },
      primary_brand_color: building.primary_brand_color,
      secondary_brand_color: building.secondary_brand_color,
    },
    validationSchema: Yup.object({
      logo: Yup.mixed()
        .test(
          'fileFormat',
          `Unsupported File Type. Valid types are ${SUPPORTED_FORMATS.join(', ')}`,
          ({ file }) => !file || (file && SUPPORTED_FORMATS.includes(file.type)),
        )
        .test('fileSize', 'File is larger than 10MB!', ({ file }) => !file || (file && file.size <= FILE_SIZE)),
    }),
    onSubmit: (values) => {
      const { logo, primary_brand_color, secondary_brand_color } = values;

      const bodyFormData = new FormData();
      bodyFormData.append('primary_brand_color', primary_brand_color);
      bodyFormData.append('secondary_brand_color', secondary_brand_color);

      if (logo?.file) {
        bodyFormData.append('logo', logo.file);
      } else if (logo?.file === null && building && building.id && building.logoUrl) {
        deleteLogo({ orgSlug, id: building.id });
      }

      const mutateProps: any = {
        id: building.id,
        orgSlug,
        building: bodyFormData,
      };

      mutate(mutateProps);
    },
  });

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      next();
    }
  }, [isLoading, isSuccess, isError]);

  return (
    <>
      <form onSubmit={formManager.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FileDropzone
              title={'Replace the current logo?'}
              imgUrl={
                formManager.values.logo && formManager.values.logo.file
                  ? URL.createObjectURL(formManager.values.logo.file)
                  : formManager.values.logo && formManager.values.logo.url
                  ? formManager.values.logo.url
                  : ''
              }
              onSelectFile={(file: File | null) => formManager.setFieldValue('logo', { file })}
              error={(formManager.touched.logo ? formManager.errors.logo : '') as string}
            />
          </Grid>

          {!formManager.values.primary_brand_color && !formManager.values.secondary_brand_color && (
            <Grid item xs={12}>
              <Button
                sx={{ paddingX: 2 }}
                startIcon={<Add />}
                onClick={() => {
                  formManager.setValues((prev) => ({
                    ...prev,
                    primary_brand_color: '#000000',
                    secondary_brand_color: '#000000',
                  }));
                }}
              >
                Add Building Brand Colors
              </Button>
            </Grid>
          )}

          {formManager.values.primary_brand_color && formManager.values.secondary_brand_color && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                <Typography variant="subtitle1" sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                  Adjust brand colors?
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  border: 1,
                  borderStyle: 'dashed',
                  borderRadius: 1,
                  borderColor: theme.palette.text.secondary,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingX: 1.75,
                }}
              >
                <BrandColorPicker
                  primaryColor={formManager.values.primary_brand_color}
                  onChangePrimaryColor={(value) => formManager.setFieldValue('primary_brand_color', value)}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    top: -14,
                    right: -14,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: '100%',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();

                      formManager.setValues((prev) => ({
                        ...prev,
                        primary_brand_color: '',
                        secondary_brand_color: '',
                      }));
                    }}
                  >
                    <Close sx={{ color: theme.palette.common.white, fontSize: 'inherit' }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <Button
                type="submit"
                sx={{ paddingX: 2 }}
                startIcon={
                  isLoading ? (
                    <Box
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <CircularProgress size={14} />
                    </Box>
                  ) : (
                    <Refresh />
                  )
                }
              >
                Refresh to see updates
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <BackdropForDialog loading={loading_form} />
    </>
  );
};
